import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import { Loading } from "@/components/common";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Sampling from "@/pages/Sampling";
import Production from "@/pages/Production";
import Planning from "@/pages/Planning";
import Departments from "@/pages/Departments";
import Printing from "@/pages/Printing";
import Stitching from "@/pages/Stitching";
import Machines from "@/pages/Machines";
import QualityControl from "@/pages/QualityControl";
import Packing from "@/pages/Packing";
import Dispatch from "@/pages/Dispatch";
import Employees from "@/pages/Employees";
import Reports from "@/pages/Reports";
import LiveBoard from "@/pages/LiveBoard";
import Notifications from "@/pages/Notifications";
import AdminSettings from "@/pages/AdminSettings";
import BuyerPortal from "@/pages/BuyerPortal";
import InnovationLab from "@/pages/InnovationLab";
import FabricDevelopment from "@/pages/FabricDevelopment";
import RndInsights from "@/pages/RndInsights";
import RestockRequests from "@/pages/RestockRequests";
import BuyerUpdatesAdmin from "@/pages/BuyerUpdatesAdmin";

function PendingApproval() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="pending-approval">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-2xl bg-warning/15 text-warning flex items-center justify-center mx-auto mb-6">
          <Clock className="w-8 h-8" />
        </div>
        <h1 className="text-3xl font-bold uppercase tracking-tight">Awaiting Approval</h1>
        <p className="text-muted-foreground mt-3">
          Hi {user?.name || user?.email}, your account is registered but needs an administrator to grant access.
          You'll be able to sign in as soon as it's approved.
        </p>
        <p className="text-xs text-muted-foreground mt-2">Signed in as {user?.email}</p>
        <Button variant="outline" onClick={logout} className="mt-6" data-testid="pending-logout-btn">Sign out</Button>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background"><Loading label="Authenticating" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === "pending") return <PendingApproval />;
  return children;
}

// Buyers are read-only external users -> keep them in the Buyer Portal only.
function StaffOnly({ children }) {
  const { user } = useAuth();
  if (user?.role === "buyer") return <Navigate to="/portal" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/portal" element={<Protected><BuyerPortal /></Protected>} />
      <Route path="/live-board" element={<Protected><StaffOnly><LiveBoard /></StaffOnly></Protected>} />
      <Route element={<Protected><StaffOnly><Layout /></StaffOnly></Protected>}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/sampling" element={<Sampling />} />
        <Route path="/innovation-lab" element={<InnovationLab />} />
        <Route path="/fabric-development" element={<FabricDevelopment />} />
        <Route path="/rnd-insights" element={<RndInsights />} />
        <Route path="/restock-requests" element={<RestockRequests />} />
        <Route path="/buyer-updates" element={<BuyerUpdatesAdmin />} />
        <Route path="/production" element={<Production />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/departments" element={<Departments />} />
        <Route path="/printing" element={<Printing />} />
        <Route path="/stitching" element={<Stitching />} />
        <Route path="/machines" element={<Machines />} />
        <Route path="/quality" element={<QualityControl />} />
        <Route path="/packing" element={<Packing />} />
        <Route path="/dispatch" element={<Dispatch />} />
        <Route path="/employees" element={<Employees />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/admin" element={<AdminSettings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <RealtimeProvider>
          <div className="App">
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </div>
        </RealtimeProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
